import React, { useState } from 'react';
import RESOURCES from '../resources/resourcesData';

// Maps the stored goalCategory value to its translation key (same keys
// OnboardingPage.js uses), so the category name follows the user's language.
const CATEGORY_KEYS = {
  'Fitness & Health':           'fitnessHealth',
  'Learning & Skills':          'learningSkills',
  'Business & Career':          'businessCareer',
  'Creative Projects':          'creativeProjects',
  'Relationships & Connection': 'relationshipsConnection',
  'Home & Life Organization':   'homeLifeOrganization',
  'Money & Financial Habits':   'moneyFinancialHabits',
  'Staying Connected':          'stayingConnected',
  'Independent Living':         'independentLiving',
  'Tech Confidence':            'techConfidence',
};

// Turns an item id like "fitness-showing-up" into the start of its
// translation key: "res_fitness_showing_up". The parts that follow are
// _title, _p1, _p2 ... for articles and _note for links.
const keyBase = (id) => 'res_' + String(id).replace(/[^a-zA-Z0-9]/g, '_');

const styleTag = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .res-article-btn {
    transition: background-color 0.2s !important;
  }
  .res-article-btn:hover {
    background-color: rgba(0,128,129,0.06) !important;
  }
  .res-link-card {
    transition: border-color 0.2s, background-color 0.2s, transform 0.15s !important;
  }
  .res-link-card:hover {
    border-color: rgba(0,128,129,0.5) !important;
    background-color: rgba(0,128,129,0.05) !important;
    transform: translateX(2px) !important;
  }
  .res-chevron {
    transition: transform 0.2s !important;
  }
`;

// Extra safety: only ever render a link if it is a plain https URL.
const isSafeUrl = (url) => typeof url === 'string' && url.startsWith('https://');

export default function ResourcesView({ userData, t: themeTokens, translate }) {
  // translate() returns the key itself when a translation is missing,
  // so "translate(key) || fallback" would not work. This helper does.
  // The fallback is the English text from resourcesData.js.
  const tr = (key, fallback) => {
    const v = translate(key);
    return !v || v === key ? fallback : v;
  };

  const [openArticles, setOpenArticles] = useState({});

  const category = userData?.goalCategory || null;
  const content =
    category && Object.prototype.hasOwnProperty.call(RESOURCES, category)
      ? RESOURCES[category]
      : null;

  const articles = (content?.articles || []).filter(a => a && a.id && a.title);
  const links = (content?.links || []).filter(l => l && l.id && l.title && isSafeUrl(l.url));
  const hasContent = articles.length > 0 || links.length > 0;

  const categoryLabel = category
    ? tr(CATEGORY_KEYS[category] || '', category)
    : '';

  const introText =
    content && content.intro
      ? tr('res_intro_' + (CATEGORY_KEYS[category] || ''), content.intro)
      : '';

  const toggleArticle = (id) => {
    setOpenArticles(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const card = {
    background: themeTokens.cardBg,
    backdropFilter: 'blur(10px)',
    borderRadius: '1.25rem',
    padding: '1.75rem',
    border: `1px solid ${themeTokens.cardBorder}`,
    animation: 'fadeInUp 0.35s ease',
  };

  const sectionLabel = {
    fontSize: '0.75rem',
    fontWeight: '700',
    color: themeTokens.subtext,
    marginBottom: '1rem',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  };

  // ── Empty / no-match state ──
  if (!hasContent) {
    return (
      <>
        <style>{styleTag}</style>
        <div style={{ maxWidth: '42rem', margin: '0 auto' }}>
          <div style={{ ...card, textAlign: 'center', padding: '3rem 1.75rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📚</div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '800', color: themeTokens.text, margin: '0 0 0.5rem', letterSpacing: '-0.02em' }}>
              {tr('resourcesEmptyTitle', 'Nothing here yet')}
            </h2>
            <p style={{ fontSize: '0.875rem', color: themeTokens.subtext, margin: 0, lineHeight: 1.6 }}>
              {content
                ? tr('resourcesEmptyBody', 'We’re still putting together resources for this category. Check back soon.')
                : tr('resourcesNoMatchBody', 'We couldn’t find resources for this goal yet.')}
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styleTag}</style>
      <div style={{ maxWidth: '42rem', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        {/* Header card */}
        <div style={card}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '800', color: themeTokens.text, margin: '0 0 0.25rem', letterSpacing: '-0.02em' }}>
            {tr('resourcesTitle', 'Resources')}
          </h2>
          <p style={{ fontSize: '0.8rem', fontWeight: '700', color: themeTokens.activeTab, margin: '0 0 0.75rem' }}>
            {categoryLabel}
          </p>
          {introText && (
            <p style={{ fontSize: '0.9rem', color: themeTokens.subtext, margin: 0, lineHeight: 1.7 }}>
              {introText}
            </p>
          )}
        </div>

        {/* Articles */}
        {articles.length > 0 && (
          <div style={{ ...card, animation: 'fadeInUp 0.4s ease' }}>
            <h3 style={sectionLabel}>{tr('resourcesArticles', 'Articles')}</h3>
            <div style={{ display: 'grid', gap: '0.6rem' }}>
              {articles.map(article => {
                const isOpen = !!openArticles[article.id];
                const base = keyBase(article.id);
                return (
                  <div
                    key={article.id}
                    style={{
                      border: `1px solid ${themeTokens.cardBorder}`,
                      borderRadius: '0.875rem',
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      className="res-article-btn"
                      onClick={() => toggleArticle(article.id)}
                      aria-expanded={isOpen}
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '1rem 1.1rem',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        color: themeTokens.text,
                        fontFamily: 'inherit',
                      }}
                    >
                      <span style={{ fontSize: '0.95rem', fontWeight: '700', lineHeight: 1.4 }}>
                        {tr(base + '_title', article.title)}
                      </span>
                      <span
                        className="res-chevron"
                        style={{
                          color: themeTokens.activeTab,
                          fontSize: '1.1rem',
                          flexShrink: 0,
                          transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}
                      >
                        ›
                      </span>
                    </button>

                    {isOpen && (
                      <div
                        style={{
                          padding: '0.25rem 1.1rem 1.1rem',
                          borderTop: `1px solid ${themeTokens.cardBorder}`,
                        }}
                      >
                        {(article.body || []).map((paragraph, i) => (
                          <p
                            key={i}
                            style={{
                              fontSize: '0.9rem',
                              color: themeTokens.text,
                              lineHeight: 1.75,
                              margin: '0.85rem 0 0',
                            }}
                          >
                            {tr(base + '_p' + (i + 1), paragraph)}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Links. Titles and sources are proper names of external pages and
            organisations, so they stay as written; the note is translated. */}
        {links.length > 0 && (
          <div style={{ ...card, animation: 'fadeInUp 0.45s ease' }}>
            <h3 style={sectionLabel}>{tr('resourcesMoreToRead', 'More to read')}</h3>
            <div style={{ display: 'grid', gap: '0.6rem' }}>
              {links.map(link => (
                <a
                  key={link.id}
                  className="res-link-card"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block',
                    padding: '1rem 1.1rem',
                    border: `1px solid ${themeTokens.cardBorder}`,
                    borderRadius: '0.875rem',
                    textDecoration: 'none',
                    color: themeTokens.text,
                  }}
                >
                  <div style={{ fontSize: '0.95rem', fontWeight: '700', lineHeight: 1.4, marginBottom: '0.2rem' }}>
                    {link.title} <span style={{ color: themeTokens.activeTab }}>↗</span>
                  </div>
                  {link.source && (
                    <div style={{ fontSize: '0.75rem', fontWeight: '600', color: themeTokens.activeTab, marginBottom: '0.4rem' }}>
                      {link.source}
                    </div>
                  )}
                  {link.note && (
                    <div style={{ fontSize: '0.82rem', color: themeTokens.subtext, lineHeight: 1.6, marginBottom: '0.4rem' }}>
                      {tr(keyBase(link.id) + '_note', link.note)}
                    </div>
                  )}
                  <div style={{ fontSize: '0.7rem', color: themeTokens.subtext }}>
                    {tr('resourcesOpensNewTab', 'Opens in a new tab')}
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Gentle disclaimer */}
        <p style={{ fontSize: '0.75rem', color: themeTokens.subtext, textAlign: 'center', lineHeight: 1.6, margin: '0 0.5rem' }}>
          {tr(
            'resourcesDisclaimer',
            'General information only, not professional advice. If something affects your health, safety or money, a qualified professional can help with your situation.'
          )}
        </p>

      </div>
    </>
  );
}